/**
 * Kalenderrekenwerk voor Mass Messages → Agenda, in Europe/Amsterdam.
 *
 * 03-10-2026 (Jordy): de agenda toont elk item op de dag en het uur zoals
 * Amsterdam het ziet, ook als de browser in een andere tijdzone staat. Datums
 * zijn hier kale kalenderdagen ("2026-10-05"); het rekenen met dagen gebeurt
 * in UTC zodat een klokwissel nooit een dag laat verspringen. Alleen de grens
 * van een dag (00:00 Amsterdam) wordt naar een echt UTC-moment omgezet, met
 * `amsterdamNaarUtc` uit amsterdam.ts.
 *
 * 07-10-2026 (Jordy): de zone is een keuze (standaard Amsterdam): de agenda
 * toont de dagen en tijden in de tijdzone van de gebruiker (lib/tijdzone.ts).
 */
import { AMSTERDAM, nlDagLabel, nlDatum, zoneDelen, zoneNaarUtc } from './amsterdam'

/** Een moment zoals Amsterdam het ziet: kalenderdag en tijd ("20:30"). */
export function amsterdamDelen(iso: string | null | undefined): { datum: string; tijd: string } | null {
  return zoneDelen(iso, AMSTERDAM)
}

/** Vandaag in Amsterdam ("2026-10-03"), of in de opgegeven zone. */
export function vandaagAmsterdam(nu: number = Date.now(), zone: string = AMSTERDAM): string {
  return (zoneDelen(new Date(nu).toISOString(), zone) as { datum: string }).datum
}

function alsUtcDag(datum: string): Date {
  const [j, m, d] = datum.split('-').map(Number)
  return new Date(Date.UTC(j, m - 1, d))
}

function dagTekst(d: Date): string {
  return d.toISOString().slice(0, 10)
}

/** Een kalenderdag plus (of min) een aantal dagen. */
export function plusDagen(datum: string, n: number): string {
  const d = alsUtcDag(datum)
  d.setUTCDate(d.getUTCDate() + n)
  return dagTekst(d)
}

/** De maandag van de week waarin deze dag valt. */
export function maandagVan(datum: string): string {
  const wd = (alsUtcDag(datum).getUTCDay() + 6) % 7 // maandag = 0
  return plusDagen(datum, -wd)
}

/** De zeven dagen (ma–zo) van de week die op deze maandag begint. */
export function weekDagen(maandag: string): string[] {
  return Array.from({ length: 7 }, (_, i) => plusDagen(maandag, i))
}

/** De weken (ma–zo) die de maand van deze dag beslaan: 4 tot 6 rijen. */
export function maandWeken(datum: string): string[][] {
  const eerste = `${datum.slice(0, 7)}-01`
  const volgende = plusDagen(eerste, 32).slice(0, 7)
  const laatste = plusDagen(`${volgende}-01`, -1)
  const weken: string[][] = []
  for (let ma = maandagVan(eerste); ma <= laatste; ma = plusDagen(ma, 7)) {
    weken.push(weekDagen(ma))
  }
  return weken
}

/** Eerste dag van de vorige/volgende maand. */
export function plusMaanden(datum: string, n: number): string {
  const [j, m] = datum.split('-').map(Number)
  const d = new Date(Date.UTC(j, m - 1 + n, 1))
  return dagTekst(d)
}

/** Het UTC-venster [van 00:00 op de eerste dag, tot 00:00 na de laatste dag). */
export function vensterUtc(eersteDag: string, laatsteDag: string, zone: string = AMSTERDAM): { from: string; to: string } {
  return {
    from: zoneNaarUtc(eersteDag, '00:00', zone) as string,
    to: zoneNaarUtc(plusDagen(laatsteDag, 1), '00:00', zone) as string,
  }
}

/** Items per kalenderdag (Amsterdam, of de opgegeven zone), binnen een dag op tijd gesorteerd. */
export function perDag<T extends { scheduled_at: string | null }>(items: T[], zone: string = AMSTERDAM): Record<string, T[]> {
  const uit: Record<string, T[]> = {}
  for (const it of items) {
    const d = zoneDelen(it.scheduled_at, zone)
    if (!d) continue
    ;(uit[d.datum] ??= []).push(it)
  }
  for (const dag of Object.keys(uit)) {
    uit[dag].sort((a, b) => Date.parse(a.scheduled_at as string) - Date.parse(b.scheduled_at as string))
  }
  return uit
}

const MAANDKOP = new Intl.DateTimeFormat('en-GB', { timeZone: 'UTC', month: 'long', year: 'numeric' })

/** "Mon 5 Oct" voor een kalenderdag. */
export function dagLabel(datum: string): string {
  // 16-10-2026 (Jordy): DD-MM-YYYY in Mass Messages ("Thu 22-10-2026").
  return nlDagLabel(datum)
}

/** "October 2026" voor de maand van deze dag. */
export function maandLabel(datum: string): string {
  return MAANDKOP.format(alsUtcDag(datum))
}

/** "19-10-2026 – 25-10-2026" voor een week (16-10-2026, Jordy: DD-MM-YYYY). */
export function weekLabel(maandag: string): string {
  return `${nlDatum(maandag)} – ${nlDatum(plusDagen(maandag, 6))}`
}
